<template>
  <div
    data-testid="direct-upload-drop"
    @click="onClick"
    @dragover.prevent
    @drop="onDrop"
  >
    <input
      ref="input"
      name="direct-upload-file"
      type="file"
      class="sf-hidden"
      data-testid="direct-upload-file"
      :multiple="multiple"
      :accept="accept"
      @change="onChange"
    />
    <slot />
  </div>
</template>

<script setup lang="ts">
import type { Blob as ActiveBlob } from '@rails/activestorage'

import { ref } from 'vue'
import { v4 as generateUuid } from 'uuid'

import { SfLog } from '@app/adapters/logger'
import { DirectUploader } from '@app/utils/direct-upload'

defineOptions({
  name: 'DirectUploadDropzone',
})

interface Props {
  onFileAdded?: ({ file, uuid }: { file: File; uuid: string }) => void
  onFileUploaded: ({ blob, uuid }: { blob: ActiveBlob; uuid: string }) => void
  onFileProgress?: ({
    uuid,
    progress,
  }: {
    uuid: string
    progress: string
  }) => void
  onFileInvalid?: ({ file, uuid }: { file: File; uuid: string }) => void
  onFileSkipped?: ({ file, uuid }: { file: File; uuid: string }) => void
  beforeUpload?: ({
    file,
    uuid,
  }: {
    file: File
    uuid: string
  }) => Promise<boolean>
  multiple?: boolean
  accept?: string
}

const props = withDefaults(defineProps<Props>(), {
  onFileAdded: () => {},
  onFileProgress: () => {},
  onFileInvalid: () => {},
  onFileSkipped: () => {},
  beforeUpload: undefined,
  multiple: false,
  accept: '',
})

const input = ref<HTMLInputElement | null>(null)

const I18n = window.I18n

const onClick = () => {
  if (input.value) {
    input.value.click()
  }
}

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const files = target.files

  if (files && files.length > 0) {
    uploadFiles(files)
    target.value = ''
  }
}

const onDrop = (e: DragEvent) => {
  e.stopPropagation()
  e.preventDefault()
  const files = e.dataTransfer?.files

  if (files && files.length > 0) {
    uploadFiles(files)
  }
}

const isAcceptedFile = (file: File) => {
  if (props.accept === '') {
    return true
  }

  const fileName = file.name.toLowerCase()
  const fileMimeType = file.type
  const fileBaseMimeType = fileMimeType.replace(/\/.*$/, '')

  return props.accept.split(',').some((validType) => {
    if (validType.charAt(0) === '.') {
      return fileName.endsWith(validType)
    } else if (/\/\*$/.test(validType)) {
      return fileBaseMimeType === validType.replace(/\/.*$/, '')
    } else {
      return fileMimeType === validType
    }
  })
}

const shouldUploadFile = (file: File) => {
  if (file.name.endsWith('.exe')) {
    return false
  }
  if (!isAcceptedFile(file)) {
    return false
  }
  const disallowedMimeTypes = ['application/vnd.microsoft.portable-executable']
  return disallowedMimeTypes.indexOf(file.type) === -1
}

const uploadFiles = (files: FileList) => {
  const newFiles = Array.from(files).map((file) => ({
    file,
    uuid: generateUuid(),
  }))

  if (props.multiple) {
    newFiles.map((file) => uploadFile(file))
  } else {
    uploadFile(newFiles[0])
  }
}

const uploadFile = async ({ file, uuid }: { file: File; uuid: string }) => {
  if (!shouldUploadFile(file)) {
    props.onFileInvalid({ file, uuid })
    return
  }

  if (props.beforeUpload) {
    const shouldProceed = await props.beforeUpload({ file, uuid })

    if (!shouldProceed) {
      props.onFileSkipped({ file, uuid })
      return
    }
  }

  props.onFileAdded({ file, uuid })

  const uploader = new DirectUploader(file, (progress) => {
    props.onFileProgress({ uuid, progress })
  })

  try {
    const blob = await uploader.upload()

    props.onFileUploaded({ blob, uuid })
  } catch (e) {
    SfLog.error('Error uploading file', { error: e })

    alert(I18n.t('js.attachments.upload_error'))
  }
}
</script>
